<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const openApp = () => {
  window.open('https://app.neurals.pro', '_blank')
}

// Lista de características para Ventas / RAG
const salesFeatures = [
  { title: 'Conexión Directa al Catálogo', desc: 'Arquitectura RAG que lee tu inventario en tiempo real para dar precios exactos sin alucinaciones.' },
  { title: 'Gestión Multilínea Integrada', desc: 'Unifica múltiples números de WhatsApp bajo un solo panel para todo tu equipo comercial.' },
  { title: 'Cierre de Ventas 24/7', desc: 'Atención cognitiva ininterrumpida, respondiendo dudas complejas durante noches y fines de semana.' }
]

// Lista de casos de uso para Captura de Leads
const leadUseCases = [
  { icon: '👤', label: 'Extracción de Nombres' },
  { icon: '📧', label: 'Captura de Correos' },
  { icon: '🎯', label: 'Cualificación en Vivo' },
  { icon: '📊', label: 'Resúmenes para CRM' },
  { icon: '📅', label: 'Agendamiento Rápido' },
  { icon: '⚙️', label: 'Soporte Técnico' }
]

// Lógica para animar la sección al hacer scroll
const sectionRef = ref(null)
const isVisible = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      isVisible.value = true
      observer.disconnect()
    }
  }, { threshold: 0.15 })

  if (sectionRef.value) {
    observer.observe(sectionRef.value)
  }
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<template>
  <section ref="sectionRef" id="captura" class="py-24 md:py-32 bg-neural-bg text-neural-text relative overflow-hidden border-b border-neural-border">

    <!-- EFECTOS DE FONDO: Clean Deep Tech Glow (Sin Cuadrícula) -->
    <div class="absolute inset-0 bg-neural-bg pointer-events-none"></div>
    <!-- Resplandor ambiental expansivo (Más suave y grande) -->
    <div class="absolute bottom-0 right-1/4 w-[45rem] h-[45rem] bg-neural-primary/10 rounded-full blur-[160px] pointer-events-none"></div>

    <div class="container mx-auto px-6 lg:px-12 relative z-10">

      <!-- CABECERA DE LA SECCIÓN -->
      <div
        class="max-w-3xl mx-auto text-center space-y-5 mb-16 md:mb-24 transition-all duration-1000 transform"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'"
      >
               <h2 class="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-neural-text leading-[1.15]">
          Soluciones cognitivas adaptadas <br class="hidden sm:inline">
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-neural-primary to-neural-accent">
            al cuello de botella de tu empresa.
          </span>
        </h2>

        <p class="text-base sm:text-lg text-neural-muted font-medium leading-relaxed max-w-2xl mx-auto">
          Nuestra infraestructura opera con la misma latencia y precisión tanto si buscas asesorar clientes con un catálogo masivo, como si necesitas perfilar miles de prospectos al mes.
        </p>
      </div>

      <!-- GRID DE DOS COLUMNAS (CASOS DE USO) -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">

        <!-- ================= COLUMNA IZQUIERDA: VENTAS Y E-COMMERCE (RAG) ================= -->
        <div
          class="relative bg-neural-card/60 backdrop-blur-xl text-neural-text rounded-[2rem] p-8 sm:p-10 lg:p-12 shadow-2xl flex flex-col justify-between border border-neural-border overflow-hidden group hover:border-neural-primary/50 transition-all duration-700 transform hover:-translate-y-2"
          :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'"
          style="transition-delay: 200ms;"
        >

          <!-- Luces ambientales en la tarjeta -->
          <div class="absolute top-0 right-0 w-80 h-80 bg-neural-primary/10 rounded-full blur-3xl pointer-events-none group-hover:bg-neural-primary/20 transition-colors duration-500"></div>
          <!-- Resplandor interno en hover -->
          <div class="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

          <div class="relative z-10">
            <!-- Etiqueta superior -->
            <div class="flex items-center justify-between mb-8">
              <span class="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-widest text-neural-primary bg-neural-primary/10 px-3 py-1.5 rounded-xl border border-neural-primary/30">
                🛒 ASESORÍA COMERCIAL
              </span>
              <span class="text-[10px] font-bold text-neural-online bg-neural-online/10 border border-neural-online/20 px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-sm">
                Catálogo Activo
              </span>
            </div>

            <!-- Titular Ventas -->
            <h3 class="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4 leading-tight">
              Cotiza y vende en piloto automático sin inventar datos.
            </h3>

            <p class="text-neural-muted text-sm sm:text-base leading-relaxed mb-8">
              Ideal para negocios con un alto volumen de consultas de precios. El agente lee tus bases de datos y asesora al cliente como si fuera tu mejor vendedor.
            </p>

            <!-- Lista de Beneficios Core -->
            <div class="space-y-4 mb-10">
              <div
                v-for="(feature, idx) in salesFeatures"
                :key="idx"
                class="flex items-start gap-4 bg-[#131316]/50 p-4 rounded-2xl border border-neural-border hover:border-neural-primary/30 hover:bg-[#131316]/80 transition-colors backdrop-blur-sm"
              >
                <div class="w-8 h-8 rounded-xl bg-neural-primary/20 flex items-center justify-center text-neural-primary shrink-0 font-black mt-0.5 border border-neural-primary/30">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <div>
                  <h4 class="text-sm font-bold text-neural-text">{{ feature.title }}</h4>
                  <p class="text-xs sm:text-sm text-neural-muted mt-0.5">{{ feature.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Botón CTA Ventas (PLG) -->
          <div class="pt-6 border-t border-neural-border relative z-10">
            <button
              @click="openApp"
              class="w-full py-4 rounded-xl bg-neural-primary hover:bg-neural-primary-hover text-white font-bold text-sm tracking-wide transition-all duration-300 shadow-glow-primary hover:shadow-[0_0_25px_rgba(99,102,241,0.6)] transform hover:-translate-y-0.5 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Desplegar mi asesor de Ventas</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </button>
            <div class="flex items-center justify-center gap-4 mt-4 text-xs font-semibold text-neural-muted">
              <span class="flex items-center gap-1.5"><svg class="w-3.5 h-3.5 text-neural-online" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg> 3 Días Gratis</span>
            </div>
          </div>

        </div>

        <!-- ================= COLUMNA DERECHA: CAPTURA DE LEADS Y SOPORTE ================= -->
        <div
          class="relative bg-[#131316]/80 backdrop-blur-xl text-neural-text rounded-[2rem] p-8 sm:p-10 lg:p-12 shadow-xl flex flex-col justify-between border border-neural-border overflow-hidden group hover:border-neural-accent/50 transition-all duration-700 transform hover:-translate-y-2"
          :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'"
          style="transition-delay: 400ms;"
        >

          <!-- Luces ambientales púrpuras (accent) -->
          <div class="absolute bottom-0 left-0 w-80 h-80 bg-neural-accent/10 rounded-full blur-3xl pointer-events-none group-hover:bg-neural-accent/20 transition-colors duration-500"></div>
          <!-- Resplandor interno en hover -->
          <div class="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

          <div class="relative z-10">
            <!-- Etiqueta superior -->
            <div class="flex items-center justify-between mb-8">
              <span class="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-widest text-neural-accent bg-neural-accent/10 px-3 py-1.5 rounded-xl border border-neural-accent/30">
                🎯 LEADS & SOPORTE
              </span>
              <span class="text-[10px] font-bold text-neural-pending bg-neural-pending/10 border border-neural-pending/20 px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-sm">
                Auditoría 24/7
              </span>
            </div>

            <!-- Titular Leads -->
            <h3 class="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4 leading-tight">
              Filtra prospectos y extrae datos de contacto al instante.
            </h3>

            <p class="text-neural-muted text-sm sm:text-base leading-relaxed mb-8">
              Diseñado para campañas de marketing y atención al cliente. El agente conversa, cualifica y genera un resumen ejecutivo estructurado para tu equipo.
            </p>

            <!-- Píldoras de Extracción Cognitiva -->
            <div class="mb-10">
              <p class="text-xs font-bold uppercase text-neural-muted tracking-wider mb-4 border-b border-neural-border/50 pb-2">
                Capacidades de Extracción:
              </p>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div
                  v-for="(item, idx) in leadUseCases"
                  :key="idx"
                  class="flex items-center gap-3 bg-neural-card/40 hover:bg-neural-card/80 backdrop-blur-sm p-3.5 rounded-xl border border-neural-border transition-colors shadow-sm group/pill"
                >
                  <span class="text-xl shrink-0 grayscale group-hover/pill:grayscale-0 transition-all">{{ item.icon }}</span>
                  <span class="text-xs sm:text-sm font-semibold text-neural-text leading-snug">{{ item.label }}</span>
                </div>
              </div>
            </div>

            <!-- Destacado de confianza -->
            <div class="bg-neural-card/40 backdrop-blur-sm rounded-xl p-4 border border-neural-border mb-8">
              <div class="flex items-center gap-2 text-xs font-bold uppercase text-neural-text">
                <span class="text-neural-accent animate-pulse">●</span> Resúmenes Ejecutivos
              </div>
              <p class="text-xs text-neural-muted mt-1.5">
                Al finalizar el chat, el agente consolida el nombre, correo y requerimiento del lead, listo para enviarse a tu CRM o asesores humanos.
              </p>
            </div>
          </div>

          <!-- Botón CTA Leads (PLG) -->
          <div class="pt-6 border-t border-neural-border relative z-10">
            <button
              @click="openApp"
              class="w-full py-4 rounded-xl bg-neural-card hover:bg-[#27272A] text-neural-text border border-neural-border hover:border-neural-accent/50 font-bold text-sm tracking-wide transition-all duration-300 shadow-md transform hover:-translate-y-0.5 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Desplegar Agente de Leads</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-neural-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </button>
            <div class="flex items-center justify-center gap-4 mt-4 text-xs font-semibold text-neural-muted">
               <span class="flex items-center gap-1.5"><svg class="w-3.5 h-3.5 text-neural-online" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg> Configuración en 2 minutos</span>
            </div>
          </div>

        </div>

      </div>

    </div>
  </section>
</template>